import { Plus, Trash } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { emptyFaq, type FaqForm } from './products-payload';

/**
 * Editor de `faqs`: la respuesta exacta que el bot debe dar a una pregunta concreta.
 *
 * Es la forma de fijar lo que el bot responde sin que el modelo improvise (envíos, materiales, garantía…)
 * **y también las dudas de DESPUÉS de la compra** (cómo se usa, cada cuánto, cuidados, conservación): el
 * asistente de postventa recibe estas mismas FAQs, filtradas a los productos que el cliente ya compró
 * (`serializeFaqs`). Las preguntas se guardan en el orden en que se escriben y el backend las mete tal
 * cual en el prompt, así que el texto de la respuesta debería ser el definitivo.
 */

export interface FaqFieldsProps {
  rows: FaqForm[];
  onChange: (rows: FaqForm[]) => void;
  disabled?: boolean;
  className?: string;
}

export function FaqFields({ rows, onChange, disabled = false, className }: FaqFieldsProps) {
  function updateRow(id: string, patch: Partial<FaqForm>) {
    onChange(rows.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  }

  function removeRow(id: string) {
    onChange(rows.filter((row) => row.id !== id));
  }

  return (
    <div className={className}>
      {rows.length === 0 ? (
        <p className="mb-3 text-caption text-ink-muted">
          Ejemplos de antes de comprar: «¿hacen envíos a todo el país?», «¿de qué material es?». Y de
          después: «¿cómo se usa?», «¿cada cuánto?», «¿cómo lo lavo?». Sin preguntas frecuentes el bot
          responde con la descripción y las reglas del producto, y si el dato no está, dice que no lo
          tiene.
        </p>
      ) : null}

      {rows.length > 0 ? (
        <div className="space-y-3">
          {rows.map((row, index) => (
            <div
              key={row.id}
              className="space-y-2 rounded-panel border border-line bg-surface-muted/40 p-3"
            >
              <div className="flex items-center gap-3">
                <Input
                  value={row.pregunta}
                  placeholder="ej. ¿Hacen envíos a todo el país?"
                  disabled={disabled}
                  aria-label={`Pregunta frecuente ${index + 1}`}
                  onChange={(event) => updateRow(row.id, { pregunta: event.target.value })}
                />

                <Button
                  variant="ghost"
                  size="icon"
                  disabled={disabled}
                  aria-label={`Eliminar la pregunta frecuente ${index + 1}`}
                  onClick={() => removeRow(row.id)}
                >
                  <Trash size={16} />
                </Button>
              </div>

              <Textarea
                value={row.respuesta}
                rows={2}
                placeholder="Respuesta exacta que debe dar el bot"
                disabled={disabled}
                aria-label={`Respuesta de la pregunta frecuente ${index + 1}`}
                onChange={(event) => updateRow(row.id, { respuesta: event.target.value })}
              />
            </div>
          ))}
        </div>
      ) : null}

      <Button
        variant="ghost"
        size="sm"
        className={rows.length > 0 ? 'mt-2' : undefined}
        disabled={disabled}
        leftIcon={<Plus size={14} />}
        onClick={() => onChange([...rows, emptyFaq()])}
      >
        Agregar pregunta
      </Button>
    </div>
  );
}
